import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { T } from '@flipster/i18n';

import { ModalPriceBasisRow } from '../../../../shared/tpsl/modal-price-basis-row';
import {
  hasSeenPositionBasisFirstTimeTooltip,
  markPositionBasisFirstTimeTooltipSeen,
} from '../../../../shared/tpsl/tpsl-basis-persistence';
import { TpslTypeScope, tpslModalBunja } from '../../../state';
import { instrumentBunja, parentPositionBunja } from '../../../state/base';
import { tpslFormSegmentBunja } from '../../segment';
import { tpslModalInputBunja } from '../../state';
import { tpslModalValidationBunja } from '../../validation';

export const TriggerPriceInput = () => {
  const {
    pricePrecisionAtom,
    quoteCurrencyAtom,
    triggerPriceInputTextAtom,
    triggerPriceTypeAtom,
    triggerBasisModeAtom,
    triggerSecondaryAtom,
    onTriggerPriceInputChange,
    onTriggerBasisModeChange,
    onTriggerSecondaryChange,
    addPercent,
    showFirstTimeTooltip,
    dismissFirstTimeTooltip,
    tpslType,
    sideAtom,
  } = useBunja(triggerPriceInputBunja);
  const { triggerPriceStatusAtom } = useBunja(tpslModalValidationBunja);
  const { trackTpslPriceEditClick } = useBunja(tpslFormSegmentBunja);
  const t = useTranslations();

  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const quoteCurrency = useAtomValue(quoteCurrencyAtom);
  const triggerPriceInputValue = useAtomValue(triggerPriceInputTextAtom);
  const [triggerPriceType, setTriggerPriceType] = useAtom(triggerPriceTypeAtom);
  const triggerBasisMode = useAtomValue(triggerBasisModeAtom);
  const triggerSecondary = useAtomValue(triggerSecondaryAtom);
  const triggerPriceStatus = useAtomValue(triggerPriceStatusAtom);
  const side = useAtomValue(sideAtom);

  const [firstTimeTooltipOpen, setFirstTimeTooltipOpen] = useState(showFirstTimeTooltip);

  useEffect(() => {
    if (!showFirstTimeTooltip) return;
    setFirstTimeTooltipOpen(true);
    // Mark on first exposure so reopen never shows again.
    dismissFirstTimeTooltip();
  }, [showFirstTimeTooltip, dismissFirstTimeTooltip]);

  const handleFirstTimeTooltipOpenChange = (open: boolean) => {
    setFirstTimeTooltipOpen(open);
  };

  return (
    <ModalPriceBasisRow
      priceValue={triggerPriceInputValue}
      onPriceChange={(value) => {
        trackTpslPriceEditClick();
        onTriggerPriceInputChange(value);
      }}
      pricePrecision={pricePrecision}
      pricePlaceholder={t('Autogen_common.r251126.s_Enter_price')}
      priceAutoFocus
      triggerPriceType={triggerPriceType}
      onTriggerPriceTypeChange={setTriggerPriceType}
      showTriggerPriceTypeSelect={false}
      basisMode={triggerBasisMode}
      onBasisModeChange={onTriggerBasisModeChange}
      secondaryValue={triggerSecondary}
      onSecondaryChange={onTriggerSecondaryChange}
      quoteCurrency={quoteCurrency}
      leg={tpslType}
      side={side}
      showQuickPercents={triggerBasisMode === 'change'}
      onQuickPercent={addPercent}
      errorMessage={triggerPriceStatus.status === 'error' ? triggerPriceStatus.message : undefined}
      firstTimeTooltip={showFirstTimeTooltip ? <T id='Trade.r260720.Roi_tp_sl_nudge_tooltip' /> : undefined}
      firstTimeTooltipOpen={showFirstTimeTooltip ? firstTimeTooltipOpen : undefined}
      onFirstTimeTooltipOpenChange={showFirstTimeTooltip ? handleFirstTimeTooltipOpenChange : undefined}
    />
  );
};

const triggerPriceInputBunja = bunja(() => {
  const tpslType = bunja.use(TpslTypeScope);
  const { pricePrecisionAtom, quoteCurrencyAtom } = bunja.use(instrumentBunja);
  const { positionType } = bunja.use(tpslModalBunja);
  const { sideAtom } = bunja.use(parentPositionBunja);
  const {
    triggerPriceInputTextAtom,
    triggerPriceTypeAtom,
    triggerBasisModeAtom,
    triggerSecondaryAtom,
    setTriggerPriceInputText,
    changeTriggerBasisMode,
    onTriggerSecondaryChange,
    addPercent,
  } = bunja.use(tpslModalInputBunja);

  const showFirstTimeTooltip = positionType === 'open' && !hasSeenPositionBasisFirstTimeTooltip();

  function onTriggerPriceInputChange(value: string): void {
    setTriggerPriceInputText(value, true);
  }

  function onTriggerBasisModeChange(mode: Parameters<typeof changeTriggerBasisMode>[0]): void {
    changeTriggerBasisMode(mode);
  }

  function dismissFirstTimeTooltip(): void {
    markPositionBasisFirstTimeTooltipSeen();
  }

  return {
    pricePrecisionAtom,
    quoteCurrencyAtom,
    triggerPriceInputTextAtom,
    triggerPriceTypeAtom,
    triggerBasisModeAtom,
    triggerSecondaryAtom,
    onTriggerPriceInputChange,
    onTriggerBasisModeChange,
    onTriggerSecondaryChange,
    addPercent,
    showFirstTimeTooltip,
    dismissFirstTimeTooltip,
    tpslType,
    sideAtom,
  };
});
triggerPriceInputBunja.debugLabel = 'triggerPriceInputBunja';
